import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Video,
  MessageSquare,
  Briefcase,
  Image,
  Users,
  FileText,
  Receipt,
  LogOut,
  Menu,
  X,
  Languages
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const navigation = [
    { name: t('Dashboard'), href: '/', icon: LayoutDashboard },
    { name: t('Testimonials'), href: '/testimonials', icon: Video },
    { name: t('Messages'), href: '/messages', icon: MessageSquare },
    { name: t('Services'), href: '/services', icon: Briefcase },
    { name: t('Portfolio'), href: '/portfolio', icon: Image },
    // { name: t('Clients'), href: '/clients', icon: Users },
    // { name: t('Quotes'), href: '/quotes', icon: FileText },
    // { name: t('Invoices'), href: '/invoices', icon: Receipt },
  ];

  const isActive = (path) => location.pathname === path;

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-md border-b border-slate-200 z-50 flex items-center justify-between px-4 shadow-xs">
        <div className="flex items-center gap-2">
          <span className="text-xl font-extrabold bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">
            Bigadsy
          </span>
          <span className="text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
            Admin
          </span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200/90 z-40
        transition-transform duration-300 lg:translate-x-0 flex flex-col shadow-xs
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 pb-4 border-b border-slate-100">
          <Link to="/" className="text-2xl font-black tracking-tight bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent inline-block">
            Bigadsy
          </Link>
          <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mt-1">Portail d'administration</p>
        </div>

        {/* <div className="px-4 py-3">
          <Button
            variant="outline"
            size="sm"
            className="w-full border-slate-200 bg-slate-50/60 text-slate-700 hover:bg-slate-100 hover:text-slate-900 justify-start font-medium text-xs rounded-lg transition-colors"
            onClick={() => setLanguage(prev => prev === 'en' ? 'fr' : 'en')}
          >
            <Languages className="h-3.5 w-3.5 mr-2 text-slate-500" />
            {language === 'en' ? 'Français' : 'English'}
          </Button>
        </div> */}

        <nav className="px-3 py-2 space-y-1 flex-1 overflow-y-auto">
          {navigation.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${isActive(item.href)
                ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white shadow-sm shadow-blue-500/25'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
            >
              <item.icon className={`h-4 w-4 ${isActive(item.href) ? 'text-white' : 'text-slate-500'}`} />
              <span>{item.name}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-100">
          <Button
            onClick={handleSignOut}
            variant="outline"
            className="w-full border-slate-200 text-slate-700 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-colors font-medium text-sm rounded-xl"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('admin.logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen bg-slate-50">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;